'use client'

import { useCallback } from 'react'

const STORAGE_KEY = 'vi-search-recommendations'
const MAX_RECOMMENDATIONS = 10

interface PopularSearch {
  term: string
  count: number
  lastSearched: number
}

const DEFAULT_RECOMMENDATIONS = [
  'playing cards',
  'card magic',
  'coin magic',
  'close-up magic',
  'mentalism',
  'cardistry',
  'magic books',
  'magic dvds',
]

export function useSearchRecommendations() {
  const getRecommendations = (): PopularSearch[] => {
    if (typeof window === 'undefined') {
      return DEFAULT_RECOMMENDATIONS.map(term => ({
        term,
        count: 1,
        lastSearched: Date.now(),
      }))
    }

    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (!stored) {
        return DEFAULT_RECOMMENDATIONS.map(term => ({
          term,
          count: 1,
          lastSearched: Date.now(),
        }))
      }

      const recommendations: PopularSearch[] = JSON.parse(stored)
      return recommendations
        .sort((a, b) => {
          // Sort by count first, then by recency
          if (b.count !== a.count) {
            return b.count - a.count
          }
          return b.lastSearched - a.lastSearched
        })
        .slice(0, MAX_RECOMMENDATIONS)
    } catch {
      return DEFAULT_RECOMMENDATIONS.map(term => ({
        term,
        count: 1,
        lastSearched: Date.now(),
      }))
    }
  }

  const trackSearch = useCallback((term: string) => {
    if (typeof window === 'undefined' || !term.trim()) {
      return
    }

    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      let recommendations: PopularSearch[] = stored
        ? JSON.parse(stored)
        : DEFAULT_RECOMMENDATIONS.map(t => ({
            term: t,
            count: 1,
            lastSearched: Date.now(),
          }))

      const normalizedTerm = term.toLowerCase().trim()
      const existing = recommendations.find(
        r => r.term.toLowerCase() === normalizedTerm
      )

      if (existing) {
        existing.count += 1
        existing.lastSearched = Date.now()
      } else {
        recommendations.push({
          term: normalizedTerm,
          count: 1,
          lastSearched: Date.now(),
        })
      }

      // Sort and limit
      recommendations = recommendations
        .sort((a, b) => {
          if (b.count !== a.count) {
            return b.count - a.count
          }
          return b.lastSearched - a.lastSearched
        })
        .slice(0, MAX_RECOMMENDATIONS * 2) // Keep more in storage

      localStorage.setItem(STORAGE_KEY, JSON.stringify(recommendations))
    } catch {
      // Ignore storage errors
    }
  }, [])

  return {
    getRecommendations,
    trackSearch,
  }
}

